import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useNavigate } from 'react-router-dom';
import './MarketerDashboard.css';

interface MarketerStats {
    referralCode: string;
    totalSignups: number;
    totalSales: number;
    totalEarnings: number;
}

export const MarketerDashboard: React.FC = () => {
    const [stats, setStats] = useState<MarketerStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isMarketer, setIsMarketer] = useState(false);
    const [payoutRequested, setPayoutRequested] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        checkMarketerAccess();
    }, []);

    const checkMarketerAccess = async () => {
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError) throw userError;

            if (!user) {
                navigate('/login');
                return;
            }

            // Check if user's email is in marketers table
            const { data: marketerData, error: marketerError } = await supabase
                .from('marketers')
                .select('id, email, is_active')
                .eq('email', user.email)
                .single();

            if (marketerError) throw marketerError;

            // Check if marketer is active
            if (!marketerData || marketerData.is_active !== true) {
                setError('You do not have access to the marketer dashboard');
                return;
            }

            setIsMarketer(true);
            fetchMarketerStats();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to verify marketer access');
            setLoading(false);
        }
    };

    const fetchMarketerStats = async () => {
        try {
            setLoading(true);
            
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError) throw userError;
            if (!user?.email) throw new Error('User email not found');
            
            // Get marketer's data
            const { data: marketerData, error: marketerError } = await supabase
                .from('marketers')
                .select('id, referral_code, is_active, total_signups, total_sales, total_earnings')
                .eq('email', user.email)
                .single();

            if (marketerError) throw marketerError;
            if (!marketerData) throw new Error('Marketer data not found');

            // Verify marketer is active
            if (marketerData.is_active !== true) throw new Error('Marketer account is not active');

            setStats({
                referralCode: marketerData.referral_code,
                totalSignups: marketerData.total_signups || 0,
                totalSales: marketerData.total_sales || 0,
                totalEarnings: marketerData.total_earnings || 0.00
            });
            
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            const { error } = await supabase.auth.signOut();
            if (error) throw error;
            navigate('/login');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error signing out');
        }
    };

    const handlePayoutRequest = async () => {
        // In a real implementation, this would connect to a payout system
        // For now, we'll just show a success message
        alert('Payout request submitted successfully! You will receive your payment within 5-7 business days.');
        setPayoutRequested(true);
    };

    if (loading) return <div className="marketer-dashboard"><div>Loading...</div></div>;
    if (error) return <div className="marketer-dashboard"><div className="error-message">Access Denied: {error}</div></div>;
    if (!isMarketer) return <div className="marketer-dashboard"><div className="error-message">You do not have access to the marketer dashboard</div></div>;
    if (!stats) return <div className="marketer-dashboard"><div>No data available</div></div>;

    const minimumPayoutThreshold = 100;
    const canRequestPayout = stats.totalEarnings >= minimumPayoutThreshold && !payoutRequested;

    return (
        <div className="marketer-dashboard">
            <div className="dashboard-header">
                <h1 className="dashboard-title">Marketer Dashboard</h1>
                <button 
                    onClick={handleSignOut}
                    className="signout-button"
                >
                    Sign Out
                </button>
            </div>
            
            <div className="stats-card">
                <div className="referral-code">
                    <h2>Your Referral Link</h2>
                    <div className="code-display">
                        {`${window.location.origin}/signup?ref=${stats.referralCode}`}
                        <button 
                            onClick={() => navigator.clipboard.writeText(`${window.location.origin}/signup?ref=${stats.referralCode}`)}
                            className="copy-button"
                        >
                            Copy Link
                        </button>
                    </div>
                    <p className="referral-link">
                        Referral Code: {stats.referralCode}
                    </p>
                </div>
                
                <div className="stats-display">
                    <h2>Performance Metrics</h2>
                    <div className="stat-item">
                        <label>Total Signups:</label>
                        <span className="stat-value">{stats.totalSignups}</span>
                    </div>
                    <div className="stat-item">
                        <label>Total Sales Made by Referrals:</label>
                        <span className="stat-value">{stats.totalSales}</span>
                    </div>
                    <div className="stat-item">
                        <label>Your Total Earnings:</label>
                        <span className="stat-value">${stats.totalEarnings.toFixed(2)}</span>
                    </div>
                    <div className="stat-item">
                        <label>Minimum Payout Threshold:</label>
                        <span className="stat-value">${minimumPayoutThreshold.toFixed(2)}</span>
                    </div>
                    <div className="payout-section">
                        <button 
                            onClick={handlePayoutRequest}
                            className={`payout-button ${canRequestPayout ? '' : 'disabled'}`}
                            disabled={!canRequestPayout}
                        >
                            {payoutRequested ? 'Payout Requested' : 'Request Payout'}
                        </button>
                        {!canRequestPayout && !payoutRequested && (
                            <p className="payout-info">
                                You need at least ${minimumPayoutThreshold.toFixed(2)} in earnings to request a payout. 
                                Current balance: ${stats.totalEarnings.toFixed(2)}
                            </p>
                        )}
                        {payoutRequested && (
                            <p className="payout-success">
                                Your payout request has been submitted successfully! You will receive your payment within 5-7 business days.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MarketerDashboard;